import { SelectOption } from 'components/inputs/BaseSelect';
import SingleSelect from 'components/inputs/dropdowns/SingleSelect';
import Label from 'components/inputs/Label';
import OverlayFooter from 'components/Overlays/OverlayFooter';
import OverlayHeader from 'components/Overlays/OverlayHeader';
import OverlayStdContainer from 'components/Overlays/OverlayStdContainer';
import AppStoreContext from 'contexts/AppStoreContext';
import Immutable from 'immutable';
import React, { useEffect, useState, useContext } from 'react';
import { UpdateMultipleSubmittals } from 'records/interactions/UpdateMultipleSubmittals';
import { Submittal } from 'records/Submittal';
import { SubmittalStatus } from 'records/SubmittalStatus';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { useTranslator } from '@bluebeam/react-i18n';

interface Props extends WithAppStoreProps {
  interaction: UpdateMultipleSubmittals;
}

type UpdatableFields = 'status';

const UpdateMultipleSubmittalsOverlay = (props: Props) => {
  const [updatedFields, setUpdatedFields] = useState(
    Immutable.Set<UpdatableFields>()
  );
  const [status, setStatus] = useState<SubmittalStatus | null>(null);

  const { actions, state } = useContext(AppStoreContext);

  const { translate } = useTranslator([
    'statusLabels',
    'buttons',
    'formLabels',
    'submittalsPage',
  ]);

  const handleCloseOverlay = (): void => {
    props.actions.clearInteractions();
  };

  useEffect(() => {
    return () => {
      actions.endEditing();
    };
  }, []);

  const handleCancelClick = async (): Promise<void> => {
    const { actions } = props;
    await actions.clearInteractions();
    actions.endEditing();
  };

  const handleSubmittalStatusChange = (kind: string): void => {
    const { appState } = props;
    const { submittalStatuses } = appState;

    const subCat = submittalStatuses.find((sc) => sc.kind === kind);
    handleFieldUpdate('status', subCat);
  };

  const handleFieldUpdate = (fieldName: string, nextValue: any): void => {
    setStatus(nextValue);

    const field: UpdatableFields = fieldName as UpdatableFields;

    setUpdatedFields(
      updatedFields.has(field)
        ? updatedFields.delete(field)
        : updatedFields.add(field)
    );

    const { actions } = props;
    actions.beginEditing();
  };

  const handleEditSubmittals = async (): Promise<void> => {
    const { selectedSubmittals, submittals } = state;

    const updatedSubmittals = submittals
      .filter((i: Submittal): boolean => selectedSubmittals.has(i.id))
      .map((p: Submittal): Submittal => {
        return updatedFields.reduce(
          (pp: Submittal, f: UpdatableFields): Submittal => {
            const state = status;
            if (state) {
              return pp.set(f, state);
            }
            return pp;
          },
          p
        );
      });
    await actions.updateSubmittals([...updatedSubmittals.values()]);

    actions.clearInteractions();
    actions.endEditing();
  };

  const getStatusText = (): string => {
    if (status != null) {
      return status.display;
    }
    const { selectedSubmittals, submittals } = state;
    if (selectedSubmittals.size === 0) {
      return translate('formLabels:selectWithDots');
    }

    const selected = submittals.filter((s) =>
      selectedSubmittals.contains(s.id)
    );
    const allSame = translatedStatuses.some((ss) => {
      return selected.every((s) => s.status.kind === ss.kind);
    });
    return allSame
      ? selected.first<Submittal>().status.display
      : translate('submittalsPage:multiple');
  };

  const { appState } = props;
  const { submittalStatuses } = appState;
  const translatedStatuses = submittalStatuses.map((status) => {
    const categoryType: `${string}:${string}` =
      `statusLabels:submittals.${status.kind}` as `${string}:${string}`;

    return new SubmittalStatus({
      kind: status.kind,
      display: translate(categoryType),
    });
  });

  const select = status?.toSelectOption();
  return (
    <OverlayStdContainer id="update-multi-sub-overlay">
      <OverlayHeader
        id="update-multi-sub-overlay-header"
        title={translate('submittalsPage:editMultiple')}
        onCancelClick={handleCloseOverlay}
      />
      <div className="column justify-between os grow-1">
        <div className="m4">
          <div className="mb3">
            <Label label={translate('statusLabels:status')} />
            <SingleSelect
              id="update-multi-sub-overlay-status"
              value={select}
              options={translatedStatuses
                .map((sc) => sc.toSelectOption())
                .toArray()}
              onChange={(sc: SelectOption): void => {
                handleSubmittalStatusChange(sc.value);
              }}
              placeholder={getStatusText()}
            />
          </div>
        </div>
      </div>
      <OverlayFooter
        id="update-multi-sub-overlay-footer"
        confirmText={translate('buttons:save')}
        onCancelClick={handleCancelClick}
        onConfirmClick={handleEditSubmittals}
        confirmDisabled={false}
      />
    </OverlayStdContainer>
  );
};

export default withAppStore<Props>(UpdateMultipleSubmittalsOverlay);
